import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { ArrowLeft, Download, RefreshCw, Search, AlertTriangle, AlertCircle, CheckCircle, Wrench, Copy, FileCode, ChevronDown, ChevronRight } from 'lucide-react';
import * as XLSX from 'xlsx';

// Lazy-initialized service to avoid Firebase initialization issues
class DiagnosticService {
  constructor() {
    this.functions = null;
  }

  initialize() {
    if (!this.functions) {
      this.functions = getFunctions();
    }
  }

  async waitForAuth() {
    return new Promise((resolve, reject) => {
      const auth = getAuth();
      const unsubscribe = auth.onAuthStateChanged(user => {
        unsubscribe();
        if (user) resolve(user);
        else reject(new Error('Not authenticated'));
      }, reject);
    });
  }

  async getDiagnosticData(startDate, endDate) {
    try {
      await this.waitForAuth();
      this.initialize();
      const getDiagnostic = httpsCallable(this.functions, 'getDataQualityDiagnostic', { timeout: 330000 });
      const result = await getDiagnostic({ startDate, endDate });
      return result.data;
    } catch (error) {
      console.error('Diagnostic query error:', error);
      throw error;
    }
  }

  async getHTSFixerSQL(startDate, endDate) {
    try {
      await this.waitForAuth();
      this.initialize();
      const getHTS = httpsCallable(this.functions, 'getHTSFixerSQL');
      const result = await getHTS({ startDate, endDate });
      return result.data;
    } catch (error) {
      console.error('HTS Fixer query error:', error);
      throw error;
    }
  }

  async getComponentCostFixerSQL(startDate, endDate) {
    try {
      await this.waitForAuth();
      this.initialize();
      const getCost = httpsCallable(this.functions, 'getComponentCostFixerSQL');
      const result = await getCost({ startDate, endDate });
      return result.data;
    } catch (error) {
      console.error('Component Cost Fixer query error:', error);
      throw error;
    }
  }

  async getDeepDiveDiagnostic(startDate, endDate) {
    try {
      await this.waitForAuth();
      this.initialize();
      const getDeepDive = httpsCallable(this.functions, 'getDeepDiveDiagnostic');
      const result = await getDeepDive({ startDate, endDate });
      return result.data;
    } catch (error) {
      console.error('Deep Dive Diagnostic query error:', error);
      throw error;
    }
  }
}

const diagnosticService = new DiagnosticService();

const DataQualityDiagnostic = ({ onBackToDashboard, embedded = false }) => {
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [startDate, setStartDate] = useState('2025-07-01');
  const [endDate, setEndDate] = useState('2025-11-30');
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  // Fix Generator State
  const [showFixGenerator, setShowFixGenerator] = useState(false);
  const [htsFixLoading, setHtsFixLoading] = useState(false);
  const [costFixLoading, setCostFixLoading] = useState(false);
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);
  const [htsFixData, setHtsFixData] = useState([]);
  const [costFixData, setCostFixData] = useState([]);
  const [deepDiveData, setDeepDiveData] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await diagnosticService.getDiagnosticData(startDate, endDate);
      setData(result.data || []);
      setSummary(result.summary || null);
    } catch (err) {
      console.error('Error fetching diagnostic data:', err);
      setError(err.message || 'Failed to load diagnostic data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Generate HTS Fix SQL
  const generateHTSFixes = useCallback(async () => {
    setHtsFixLoading(true);
    try {
      const result = await diagnosticService.getHTSFixerSQL(startDate, endDate);
      setHtsFixData(result.data || []);
    } catch (err) {
      console.error('Error generating HTS fixes:', err);
      setError('Failed to generate HTS fixes: ' + err.message);
    } finally {
      setHtsFixLoading(false);
    }
  }, [startDate, endDate]);

  // Generate Component Cost Fix SQL
  const generateCostFixes = useCallback(async () => {
    setCostFixLoading(true);
    try {
      const result = await diagnosticService.getComponentCostFixerSQL(startDate, endDate);
      setCostFixData(result.data || []);
    } catch (err) {
      console.error('Error generating cost fixes:', err);
      setError('Failed to generate cost fixes: ' + err.message);
    } finally {
      setCostFixLoading(false);
    }
  }, [startDate, endDate]);

  // Run Deep Dive Diagnostic
  const runDeepDive = useCallback(async () => {
    setDeepDiveLoading(true);
    try {
      const result = await diagnosticService.getDeepDiveDiagnostic(startDate, endDate);
      setDeepDiveData(result.data || []);
    } catch (err) {
      console.error('Error running deep dive:', err);
      setError('Failed to run deep dive: ' + err.message);
    } finally {
      setDeepDiveLoading(false);
    }
  }, [startDate, endDate]);

  // Load on mount
  useEffect(() => {
    fetchData();
  }, []);

  // Filtered data
  const filteredData = useMemo(() => {
    let result = data;
    if (severityFilter !== 'ALL') {
      result = result.filter(row => row.severity === severityFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(row =>
        row.commercial_invoice?.toLowerCase().includes(term) ||
        row.hts_codes_needing_fix?.toLowerCase().includes(term) ||
        row.styles_with_zero_costs?.toLowerCase().includes(term)
      );
    }
    return result;
  }, [data, severityFilter, searchTerm]);

  // Severity counts
  const severityCounts = useMemo(() => {
    const counts = { CRITICAL: 0, HIGH_HTS: 0, HIGH_COST: 0, OK: 0 };
    data.forEach(row => {
      if (counts[row.severity] !== undefined) counts[row.severity]++;
    });
    return counts;
  }, [data]);

  // Export to Excel
  const handleExportExcel = () => {
    const exportData = filteredData.map(row => ({
      'Commercial Invoice': row.commercial_invoice,
      'Severity': row.severity === 'CRITICAL' ? 'CRITICAL - Multiple Issues' :
                  row.severity === 'HIGH_HTS' ? 'HIGH - HTS Mismatch' :
                  row.severity === 'HIGH_COST' ? 'HIGH - Zero Component Costs' : 'OK',
      'HTS Mismatch Count': row.hts_mismatch_count || 0,
      'Zero Cost Lines': row.zero_component_cost_lines || 0,
      'Total Customs Value': row.total_customs_value || 0,
      'Total Production FOB': row.total_production_fob || 0,
      'Unallocated Customs $': row.unallocated_customs_value || 0,
      '% Customs Unallocated': row.pct_customs_unallocated || 0,
      'HTS Codes Needing Fix': row.hts_codes_needing_fix || '',
      'Styles with Zero Costs': row.styles_with_zero_costs || '',
      'Action Required': row.action_required || ''
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data Quality Issues');
    XLSX.writeFile(wb, `Data_Quality_Diagnostic_${startDate}_to_${endDate}.xlsx`);
  };

  // Copy SQL to clipboard
  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Generate full SQL script
  const generateFullScript = () => {
    const header = `-- ============================================================================
-- MASTER DATA QUALITY FIX SCRIPT
-- Generated: ${new Date().toISOString().split('T')[0]}
-- Date Range: ${startDate} to ${endDate}
-- Purpose: Fix HTS typos and missing component costs
-- ============================================================================

`;

    const htsSection = htsFixData.length > 0 ? `
-- ============================================================================
-- SECTION 1: HTS CODE TYPO FIXES IN duty_charges_data
-- Total: ${htsFixData.length} fixes
-- ============================================================================

${htsFixData.map(row => `${row.comment_line}\n${row.sql_statement}`).join('\n\n')}

` : '';

    const costSection = costFixData.length > 0 ? `
-- ============================================================================
-- SECTION 2: MISSING COMPONENT COSTS IN style_hts_data
-- Total: ${costFixData.length} fixes
-- ============================================================================

${costFixData.map(row => `${row.comment_line}\n${row.sql_statement}`).join('\n\n')}

` : '';

    return header + htsSection + costSection;
  };

  // Download SQL file
  const downloadSQLFile = () => {
    const script = generateFullScript();
    const blob = new Blob([script], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `data_quality_fixes_${startDate}_to_${endDate}.sql`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Copy full script to clipboard
  const copyFullScript = () => {
    const script = generateFullScript();
    navigator.clipboard.writeText(script);
    setCopiedIndex('full');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  // Severity badge component
  const SeverityBadge = ({ severity }) => {
    const config = {
      CRITICAL: { bg: 'bg-red-100', color: 'text-red-700', border: 'border-red-200', icon: AlertCircle, text: 'CRITICAL' },
      HIGH_HTS: { bg: 'bg-amber-100', color: 'text-amber-700', border: 'border-amber-200', icon: AlertTriangle, text: 'HTS Mismatch' },
      HIGH_COST: { bg: 'bg-amber-100', color: 'text-amber-700', border: 'border-amber-200', icon: AlertTriangle, text: 'Zero Costs' },
      OK: { bg: 'bg-green-100', color: 'text-green-700', border: 'border-green-200', icon: CheckCircle, text: 'OK' }
    };
    const cfg = config[severity] || config.OK;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
        <Icon size={12} />
        {cfg.text}
      </span>
    );
  };

  return (
    <div className={embedded ? "bg-slate-50 p-4" : "p-6 bg-slate-50 min-h-screen"}>
      {/* Header */}
      <div className="mb-6">
        {!embedded && onBackToDashboard && (
          <button onClick={onBackToDashboard} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 text-sm">
            <ArrowLeft size={16} /> Back to Dashboard
          </button>
        )}
        {!embedded && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Data Quality Diagnostic</h1>
            <p className="text-gray-500 text-sm">
              Master diagnostic to find all commercial invoices with HTS mismatches or zero component costs
            </p>
          </>
        )}
      </div>

      {/* Date Range & Controls */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <button onClick={fetchData} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Loading...' : 'Run Diagnostic'}
          </button>
          <button onClick={handleExportExcel} disabled={filteredData.length === 0}
            className="flex items-center gap-2 px-4 py-2 border border-green-600 text-green-600 text-sm font-medium rounded-lg hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed">
            <Download size={16} /> Export Excel
          </button>
          <button onClick={() => setShowFixGenerator(!showFixGenerator)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              showFixGenerator ? 'bg-purple-100 text-purple-700 border-purple-300' : 'bg-white text-purple-600 border-purple-300 hover:bg-purple-50'
            }`}>
            <Wrench size={16} />
            {showFixGenerator ? 'Hide' : 'Show'} Fix Generator
            {showFixGenerator ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>
      </div>

      {/* Fix Generator Section */}
      {showFixGenerator && (
        <div className="bg-white rounded-xl border border-purple-200 shadow-sm p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Wrench className="text-purple-600" size={20} />
              SQL Fix Generator
            </h2>
            <div className="flex gap-2">
              {(htsFixData.length > 0 || costFixData.length > 0) && (
                <>
                  <button onClick={copyFullScript}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border ${
                      copiedIndex === 'full' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                    }`}>
                    <Copy size={14} />
                    {copiedIndex === 'full' ? 'Copied!' : 'Copy All SQL'}
                  </button>
                  <button onClick={downloadSQLFile}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700">
                    <FileCode size={14} /> Download .sql
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* HTS Fixer Card */}
            <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/30">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-800">🏷️ HTS Code Fixes</h3>
                <button onClick={generateHTSFixes} disabled={htsFixLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-amber-600 text-white hover:bg-amber-700 disabled:bg-gray-400">
                  <RefreshCw size={12} className={htsFixLoading ? 'animate-spin' : ''} />
                  {htsFixLoading ? 'Generating...' : 'Generate'}
                </button>
              </div>
              <p className="text-xs text-gray-600 mb-2">
                Finds HTS typos in customs data and generates UPDATE statements to fix them.
              </p>
              {htsFixData.length > 0 && (
                <div className="text-sm font-medium text-amber-700">
                  ✅ {htsFixData.length} fix{htsFixData.length !== 1 ? 'es' : ''} generated
                </div>
              )}
            </div>

            {/* Component Cost Fixer Card */}
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/30">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-800">💰 Component Cost Fixes</h3>
                <button onClick={generateCostFixes} disabled={costFixLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400">
                  <RefreshCw size={12} className={costFixLoading ? 'animate-spin' : ''} />
                  {costFixLoading ? 'Generating...' : 'Generate'}
                </button>
              </div>
              <p className="text-xs text-gray-600 mb-2">
                Calculates missing component costs and generates UPDATE statements.
              </p>
              {costFixData.length > 0 && (
                <div className="text-sm font-medium text-blue-700">
                  ✅ {costFixData.length} fix{costFixData.length !== 1 ? 'es' : ''} generated
                </div>
              )}
            </div>

            {/* Deep Dive Diagnostic Card */}
            <div className="border border-red-200 rounded-lg p-4 bg-red-50/30">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-800">🔍 Deep Dive</h3>
                <button onClick={runDeepDive} disabled={deepDiveLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400">
                  <RefreshCw size={12} className={deepDiveLoading ? 'animate-spin' : ''} />
                  {deepDiveLoading ? 'Analyzing...' : 'Analyze'}
                </button>
              </div>
              <p className="text-xs text-gray-600 mb-2">
                Analyzes WHY issues can't be auto-fixed. Shows root causes and recommended actions.
              </p>
              {deepDiveData.length > 0 && (
                <div className="text-sm font-medium text-red-700">
                  🔍 {deepDiveData.length} issue{deepDiveData.length !== 1 ? 's' : ''} analyzed
                </div>
              )}
            </div>
          </div>

          {/* Deep Dive Results */}
          {deepDiveData.length > 0 && (
            <div className="border border-red-200 rounded-lg overflow-hidden mb-4">
              <div className="bg-red-800 text-white px-4 py-2 text-sm font-medium flex justify-between items-center">
                <span>🔍 Deep Dive Analysis - Why Can't Issues Be Auto-Fixed?</span>
                <span className="text-red-200">{deepDiveData.length} issues</span>
              </div>
              <div className="max-h-80 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-red-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Invoice</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Problem HTS</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Amount</th>
                      <th className="px-3 py-2 text-center font-semibold text-gray-700">Status</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Root Cause</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deepDiveData.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 font-medium text-gray-900">{row.commercial_invoice}</td>
                        <td className="px-3 py-2 font-mono text-gray-700">{row.problem_hts_in_customs}</td>
                        <td className="px-3 py-2 text-right text-gray-700">${row.unallocated_amount?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            row.production_status === 'NO_PRODUCTION' ? 'bg-red-100 text-red-700' :
                            row.production_status === 'NO_HTS_MAPPING' ? 'bg-orange-100 text-orange-700' :
                            row.production_status === 'HAS_DATA' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {row.production_status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600">{row.root_cause}</td>
                        <td className="px-3 py-2 text-gray-600">{row.recommended_action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Generated SQL Display */}
          {(htsFixData.length > 0 || costFixData.length > 0) && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-800 text-gray-100 px-4 py-2 text-xs font-mono flex justify-between items-center">
                <span>Generated SQL Statements</span>
                <span className="text-gray-400">{htsFixData.length + costFixData.length} total</span>
              </div>
              <div className="max-h-96 overflow-y-auto bg-gray-900 p-4">
                {htsFixData.length > 0 && (
                  <>
                    <div className="text-amber-400 text-xs font-mono mb-2">-- SECTION 1: HTS CODE FIXES ({htsFixData.length})</div>
                    {htsFixData.map((row, idx) => (
                      <div key={`hts-${idx}`} className="mb-3 group">
                        <div className="text-gray-500 text-xs font-mono">{row.comment_line}</div>
                        <div className="flex items-start gap-2">
                          <code className="text-green-400 text-xs font-mono flex-1 break-all">{row.sql_statement}</code>
                          <button onClick={() => copyToClipboard(row.sql_statement, `hts-${idx}`)}
                            className={`opacity-0 group-hover:opacity-100 p-1 rounded ${
                              copiedIndex === `hts-${idx}` ? 'text-green-400' : 'text-gray-400 hover:text-white'
                            }`}>
                            <Copy size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {costFixData.length > 0 && (
                  <>
                    <div className="text-blue-400 text-xs font-mono mb-2 mt-4">-- SECTION 2: COMPONENT COST FIXES ({costFixData.length})</div>
                    {costFixData.map((row, idx) => (
                      <div key={`cost-${idx}`} className="mb-3 group">
                        <div className="text-gray-500 text-xs font-mono">{row.comment_line}</div>
                        <div className="flex items-start gap-2">
                          <code className="text-green-400 text-xs font-mono flex-1 break-all">{row.sql_statement}</code>
                          <button onClick={() => copyToClipboard(row.sql_statement, `cost-${idx}`)}
                            className={`opacity-0 group-hover:opacity-100 p-1 rounded ${
                              copiedIndex === `cost-${idx}` ? 'text-green-400' : 'text-gray-400 hover:text-white'
                            }`}>
                            <Copy size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700 flex items-center gap-2">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-center">
            <div className="text-3xl font-bold text-blue-600">{summary.totalInvoicesInPeriod}</div>
            <div className="text-xs text-gray-500 mt-1">Total Invoices</div>
          </div>
          <div className="bg-white rounded-xl border border-red-200 shadow-sm p-5 text-center">
            <div className="text-3xl font-bold text-red-600">{summary.totalInvoicesNeedingFixes}</div>
            <div className="text-xs text-gray-500 mt-1">Need Fixes</div>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-5 text-center">
            <div className="text-3xl font-bold text-amber-600">{summary.invoicesWithHtsIssues}</div>
            <div className="text-xs text-gray-500 mt-1">HTS Issues</div>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-5 text-center">
            <div className="text-3xl font-bold text-amber-600">{summary.invoicesWithCostIssues}</div>
            <div className="text-xs text-gray-500 mt-1">Zero Cost Issues</div>
          </div>
          <div className="bg-white rounded-xl border border-red-200 shadow-sm p-5 text-center">
            <div className="text-2xl font-bold text-red-600">{formatCurrency(summary.totalUnallocatedDutyDollars)}</div>
            <div className="text-xs text-gray-500 mt-1">Unallocated Duty $</div>
          </div>
        </div>
      )}

      {/* Severity Filter Tabs & Search */}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        {[
          { key: 'ALL', label: 'All Issues', count: data.length, color: 'blue' },
          { key: 'CRITICAL', label: 'Critical', count: severityCounts.CRITICAL, color: 'red' },
          { key: 'HIGH_HTS', label: 'HTS Mismatch', count: severityCounts.HIGH_HTS, color: 'amber' },
          { key: 'HIGH_COST', label: 'Zero Costs', count: severityCounts.HIGH_COST, color: 'amber' }
        ].map(tab => (
          <button key={tab.key} onClick={() => setSeverityFilter(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              severityFilter === tab.key
                ? `bg-${tab.color}-100 text-${tab.color}-700 border-${tab.color}-300`
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}>
            {tab.label}
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${severityFilter === tab.key ? `bg-${tab.color}-200` : 'bg-gray-100'}`}>
              {tab.count}
            </span>
          </button>
        ))}
        <div className="flex-1 min-w-[200px] max-w-md ml-auto">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search invoice, HTS, or style..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>
      </div>

      {/* Results Table */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
          <RefreshCw size={48} className="mx-auto text-blue-500 animate-spin mb-4" />
          <div className="text-lg font-medium text-gray-700">Running diagnostic query...</div>
          <div className="text-sm text-gray-500 mt-1">This may take 30-60 seconds</div>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
          <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
          <div className="text-lg font-medium text-gray-700">No issues found!</div>
          <div className="text-sm text-gray-500 mt-1">All invoices in this date range are clean.</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 flex justify-between items-center">
            <span className="font-semibold text-gray-700">
              {filteredData.length} Invoice{filteredData.length !== 1 ? 's' : ''} with Issues
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Invoice</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Severity</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">HTS Mismatches</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Zero Cost Lines</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">Customs Value</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">Production FOB</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">Unallocated $</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">% Unalloc</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Action Required</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, idx) => (
                  <React.Fragment key={row.commercial_invoice || idx}>
                    <tr className={`border-b border-gray-100 hover:bg-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                      <td className="px-4 py-3 font-medium text-gray-900">{row.commercial_invoice}</td>
                      <td className="px-4 py-3 text-center"><SeverityBadge severity={row.severity} /></td>
                      <td className={`px-4 py-3 text-center ${row.hts_mismatch_count > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                        {row.hts_mismatch_count || 0}
                      </td>
                      <td className={`px-4 py-3 text-center ${row.zero_component_cost_lines > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                        {row.zero_component_cost_lines || 0}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(row.total_customs_value)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(row.total_production_fob)}</td>
                      <td className={`px-4 py-3 text-right ${row.unallocated_customs_value > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                        {formatCurrency(row.unallocated_customs_value)}
                      </td>
                      <td className={`px-4 py-3 text-center ${(row.pct_customs_unallocated || 0) > 10 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                        {row.pct_customs_unallocated ? `${row.pct_customs_unallocated}%` : '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{row.action_required}</td>
                    </tr>
                    {row.hts_codes_needing_fix && (
                      <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}>
                        <td colSpan="9" className="px-4 py-2 pl-8">
                          <span className="text-amber-600 text-xs font-medium">🏷️ HTS Codes Needing Fix: </span>
                          <span className="text-gray-600 text-xs font-mono">{row.hts_codes_needing_fix}</span>
                        </td>
                      </tr>
                    )}
                    {row.styles_with_zero_costs && (
                      <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}>
                        <td colSpan="9" className="px-4 py-2 pl-8 pb-4">
                          <span className="text-amber-600 text-xs font-medium">👗 Styles with Zero Costs: </span>
                          <span className="text-gray-600 text-xs">{row.styles_with_zero_costs}</span>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataQualityDiagnostic;
